/**
 * Shared bulk update for prepared drafts (merge list): schedule intervals + per-drop creative expansion.
 */

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import type { BulkUpdatePreparedDropsPayload } from '@/hooks';

import {
  bulkUpdateNeedsPerItemCreative,
  stripPerDropCreativeFields,
  withPerDropRowApplied,
} from '@/features/smswiser-campaigns/utils/bulkUpdatePerDropCreative';
import { groupItemsByCountryKey } from '@/features/smswiser-campaigns/utils/preparedDropBulkByCountry';
import { applyScheduleInterval } from '@/features/smswiser-campaigns/utils/scheduleInterval';

export async function executeBulkUpdatePreparedDrops(params: {
  ids: string[];
  drops: PreparedDrop[];
  payload: BulkUpdatePreparedDropsPayload;
  scheduleIntervalMinutes?: number;
  mutateAsync: (args: {
    countryKey: string;
    items: Array<{ id: string; wiserId: number }>;
    payload: BulkUpdatePreparedDropsPayload;
  }) => Promise<unknown>;
}): Promise<void> {
  const { ids, drops, payload, scheduleIntervalMinutes, mutateAsync } = params;
  const items = ids
    .map((id) => drops.find((d) => d.id === id))
    .filter((d): d is PreparedDrop => Boolean(d))
    .map((d) => ({ id: d.id, wiserId: d.wiserId }));
  if (items.length === 0) return;

  const basePayload = stripPerDropCreativeFields(payload);
  const expandCreative = bulkUpdateNeedsPerItemCreative(items.length, payload);
  const pt = payload.smsTextsPerDrop;
  const ps = payload.senderNamesPerDrop;

  if (scheduleIntervalMinutes && scheduleIntervalMinutes > 0 && payload.executeAfter) {
    for (let i = 0; i < items.length; i++) {
      let perItemPayload: BulkUpdatePreparedDropsPayload = {
        ...basePayload,
        executeAfter: applyScheduleInterval(payload.executeAfter, scheduleIntervalMinutes, i),
      };
      if (expandCreative) perItemPayload = withPerDropRowApplied(perItemPayload, i, pt, ps);
      const row = drops.find((d) => d.id === items[i]!.id);
      if (!row?.countryKey) continue;
      await mutateAsync({
        countryKey: row.countryKey,
        items: [items[i]!],
        payload: perItemPayload,
      });
    }
    return;
  }

  if (expandCreative) {
    for (let i = 0; i < items.length; i++) {
      const perItemPayload = withPerDropRowApplied({ ...basePayload }, i, pt, ps);
      const row = drops.find((d) => d.id === items[i]!.id);
      if (!row?.countryKey) continue;
      await mutateAsync({
        countryKey: row.countryKey,
        items: [items[i]!],
        payload: perItemPayload,
      });
    }
    return;
  }

  const byCountry = groupItemsByCountryKey(items, drops);
  for (const [ck, groupItems] of byCountry) {
    await mutateAsync({ countryKey: ck, items: groupItems, payload: basePayload });
  }
}
